import { createRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input } from '@arabquiz/ui'
import { Route as rootRoute } from '../__root'
import { mapServerMessageToKey } from './auth-errors'

/**
 * Sign-in route — Arabic, RTL, validation feedback.
 *
 * Uses TanStack Form for state + field-level validation, TanStack Query for
 * the POST mutation against `/api/v1/auth/sign-in`. Validation error keys are
 * resolved from the `validation` i18n namespace; UI labels from `auth`.
 *
 * The page is rendered inside `<html dir="rtl">` (set in `index.html`), so all
 * spacing utilities use logical axes (`px-/py-/mb-/space-y-*`). Email and
 * password inputs are forced to `dir="ltr"` because Latin credentials must
 * render left-to-right even on an RTL page.
 */
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/sign-in',
  // Capture an optional post-login return path. Only same-origin absolute
  // paths ("/...") are accepted — anything else (protocol-relative "//evil",
  // absolute URLs, empty) collapses to undefined so we can never be coerced
  // into an open redirect.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const raw = search['redirect']
    if (typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//')) {
      return { redirect: raw }
    }
    return {}
  },
  component: SignInPage,
})

type SignInPayload = {
  email: string
  password: string
  rememberMe: boolean
}

type ValidationKey = 'required' | 'invalidEmail' | 'passwordTooShort' | 'passwordMismatch'

/**
 * Minimal identity shape this page reads from the shared `['auth', 'me']`
 * cache. We only need a truthy/null signal to decide the redirect, so the
 * exact field set is irrelevant — mirrors `_app.tsx`'s `MeResponse`.
 */
type MeResponse = {
  userId: string
  email: string
  displayName: string
  emailConfirmed: boolean
  organizationId: string
  locale: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Structured error thrown by the sign-in mutation. Carries either the RFC7807
 * ProblemDetails field-errors map (FluentValidation) or the single business
 * `error` code (e.g. `EmailOrPasswordInvalid`, `AccountLocked`), so the UI can
 * render localized Arabic messages instead of a generic catch-all.
 */
class SignInError extends Error {
  readonly fieldErrors?: Record<string, string[]> | undefined
  readonly errorCode?: string | undefined

  constructor(detail: { fieldErrors?: Record<string, string[]>; errorCode?: string }) {
    super('sign-in-failed')
    this.name = 'SignInError'
    this.fieldErrors = detail.fieldErrors
    this.errorCode = detail.errorCode
  }
}

function SignInPage() {
  const { t } = useTranslation('auth')
  const { t: tValidation } = useTranslation('validation')
  const { t: tCommon } = useTranslation('common')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Where to land after a successful sign-in: the page the user was on when
  // their session expired (captured by the `_app` auth gate), or the
  // dashboard for a fresh sign-in. `validateSearch` has already sanitised
  // this to a safe same-origin path or undefined.
  const { redirect } = Route.useSearch()
  const postLoginTarget = redirect ?? '/dashboard'

  // Reuse the shared `['auth', 'me']` cache (same key/shape as `_app.tsx`):
  // if an already-signed-in visitor lands here we bounce them to the app
  // instead of showing a redundant sign-in form. 401/non-ok resolves to
  // `null` (anonymous) rather than throwing — this page is fine for guests.
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/auth/me', {
        credentials: 'include',
        signal,
      })
      if (res.status === 401) return null
      if (!res.ok) return null
      return (await res.json()) as MeResponse
    },
    retry: false,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (meQuery.data) {
      void navigate({ to: postLoginTarget })
    }
  }, [meQuery.data, navigate, postLoginTarget])

  const signInMutation = useMutation({
    mutationFn: async (payload: SignInPayload) => {
      const res = await fetch('/api/v1/auth/sign-in', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      })
      if (!res.ok) {
        // Three possible failure shapes:
        //  1. RFC7807 ProblemDetails -> { errors: { Field: ["..."] } }
        //  2. Bad credentials        -> 401 { error: "EmailOrPasswordInvalid" }
        //  3. Locked out             -> 423 { error: "AccountLocked" }
        let body: unknown = null
        try {
          body = await res.json()
        } catch {
          // Non-JSON body (unexpected); fall through to the generic error.
        }

        if (body && typeof body === 'object') {
          const problem = body as {
            errors?: Record<string, string[]>
            error?: string
          }
          if (problem.errors && typeof problem.errors === 'object') {
            throw new SignInError({ fieldErrors: problem.errors })
          }
          if (typeof problem.error === 'string') {
            throw new SignInError({ errorCode: problem.error })
          }
        }

        throw new SignInError({})
      }
      return res.json() as Promise<{ id: string; email: string; displayName: string }>
    },
    onSuccess: async () => {
      // Drop any stale (e.g. unauthorized) `/me` result so the `_app` auth
      // gate refetches the now-authenticated session instead of reusing a
      // cached signed-out state.
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      // Land back where the user was when their session expired (if we have a
      // sanitised return path), otherwise the authenticated dashboard — not
      // the public marketing page at `/`, which always renders a "sign in"
      // link and so looks signed-out even when the session cookie is valid.
      void navigate({ to: postLoginTarget })
    },
  })

  const form = useForm({
    defaultValues: { email: '', password: '', rememberMe: false } as SignInPayload,
    onSubmit: async ({ value }) => {
      await signInMutation.mutateAsync(value)
    },
  })

  // Translate the structured server error into a de-duplicated list of Arabic
  // messages. Unknown server strings fall back to the generic error so the box
  // never renders raw English or breaks.
  const errorMessages: string[] = (() => {
    const error = signInMutation.error
    if (!(error instanceof SignInError)) return [tCommon('error')]

    if (error.errorCode === 'EmailOrPasswordInvalid') {
      return [tValidation('emailOrPasswordInvalid')]
    }
    if (error.errorCode === 'AccountLocked') {
      return [tValidation('accountLocked')]
    }

    if (error.fieldErrors) {
      const messages = Object.values(error.fieldErrors)
        .flat()
        .map((serverMessage) => {
          const key = mapServerMessageToKey(serverMessage)
          return key ? tValidation(key) : tCommon('error')
        })
      const unique = [...new Set(messages)]
      if (unique.length > 0) return unique
    }

    return [tCommon('error')]
  })()

  // While identity is resolving, or once we know the visitor is signed in
  // (redirect scheduled in the effect above), render nothing so we never
  // flash the sign-in form to an authenticated user.
  if (meQuery.isLoading || meQuery.data) {
    return null
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-bl from-brand-50/60 via-white to-brand-100/30 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl border border-brand-100 p-8 sm:p-10">
          <div className="mb-8 text-center">
            <div
              className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
              aria-hidden="true"
            />
            <h1 className="text-3xl font-bold font-display text-brand-900 tracking-tight">
              {t('signIn')}
            </h1>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void form.handleSubmit()
            }}
            noValidate
          >
            <div className="space-y-5">
              <form.Field
                name="email"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) return 'required'
                    if (!EMAIL_PATTERN.test(value)) return 'invalidEmail'
                    return undefined
                  },
                }}
              >
                {(field) => {
                  const firstError = field.state.meta.errors[0]
                  const showError =
                    field.state.meta.isTouched && typeof firstError === 'string'
                  return (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-semibold text-brand-900 mb-1.5"
                      >
                        {t('email')}
                      </label>
                      <Input
                        id={field.name}
                        type="email"
                        name={field.name}
                        autoComplete="email"
                        dir="ltr"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        aria-invalid={showError || undefined}
                        aria-describedby={showError ? `${field.name}-error` : undefined}
                      />
                      {showError ? (
                        <p
                          id={`${field.name}-error`}
                          className="text-xs text-red-600 mt-1.5"
                          role="alert"
                        >
                          {tValidation(firstError as ValidationKey)}
                        </p>
                      ) : null}
                    </div>
                  )
                }}
              </form.Field>

              <form.Field
                name="password"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) return 'required'
                    return undefined
                  },
                }}
              >
                {(field) => {
                  const firstError = field.state.meta.errors[0]
                  const showError =
                    field.state.meta.isTouched && typeof firstError === 'string'
                  return (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-semibold text-brand-900 mb-1.5"
                      >
                        {t('password')}
                      </label>
                      <Input
                        id={field.name}
                        type="password"
                        name={field.name}
                        autoComplete="current-password"
                        dir="ltr"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        aria-invalid={showError || undefined}
                        aria-describedby={showError ? `${field.name}-error` : undefined}
                      />
                      {showError ? (
                        <p
                          id={`${field.name}-error`}
                          className="text-xs text-red-600 mt-1.5"
                          role="alert"
                        >
                          {tValidation(firstError as ValidationKey)}
                        </p>
                      ) : null}
                    </div>
                  )
                }}
              </form.Field>

              <div className="flex items-center justify-between gap-3">
                <form.Field name="rememberMe">
                  {(field) => (
                    <label className="flex items-center gap-2 text-sm text-brand-800 select-none cursor-pointer">
                      <input
                        type="checkbox"
                        checked={field.state.value}
                        onChange={(e) => field.handleChange(e.target.checked)}
                        className="size-4 rounded border-brand-300 text-brand-500 focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                      />
                      <span>{t('rememberMe')}</span>
                    </label>
                  )}
                </form.Field>
                <a
                  href="/auth/forgot-password"
                  className="text-sm font-semibold text-brand-600 hover:text-brand-700 hover:underline"
                >
                  {t('forgotPassword')}
                </a>
              </div>

              {signInMutation.isError ? (
                <div
                  className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2"
                  role="alert"
                >
                  <ul className="list-disc space-y-1 ps-5">
                    {errorMessages.map((message, index) => (
                      <li key={index}>{message}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <Button
                type="submit"
                size="lg"
                disabled={signInMutation.isPending}
                className="w-full"
              >
                {signInMutation.isPending ? tCommon('loading') : t('signIn')}
              </Button>
            </div>
          </form>

          <p className="mt-6 text-center text-sm text-brand-800">
            <span>{t('noAccount')} </span>
            <a
              href="/auth/sign-up"
              className="font-semibold text-brand-600 hover:text-brand-700 hover:underline"
            >
              {t('signUp')}
            </a>
          </p>
        </div>
      </div>
    </main>
  )
}
